import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Brain, Plus, Mail, ShieldCheck, LogIn, LogOut, ChevronDown, Sparkles, Menu, X } from "lucide-react";
import { Wordmark } from "./Wordmark";
import { useAuth } from "./AuthContext";
import { AuthModal } from "./AuthModal";
import { CONTACTS } from "@/lib/networkData";
import { WhatsAppIcon } from "./WhatsAppIcon";

// Compact IT/EN language toggle + small WhatsApp icon, sits next to the header logo.
const HeaderLangWhatsApp = ({ settings, onSettingsChange }) => {
  const lang = (settings?.language || "").toLowerCase();
  const isIT = lang.startsWith("ital");
  const isEN = lang.startsWith("eng");
  const setLang = (l) => onSettingsChange?.({ ...(settings || {}), language: l });
  return (
    <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
      <div data-testid="lang-toggle" className="flex items-center bg-secondary/70 border border-border rounded-full p-0.5">
        <button
          data-testid="lang-it"
          onClick={() => setLang("Italiano")}
          className={`px-2.5 py-1 text-xs font-semibold rounded-full transition-colors ${isIT ? "bg-blue-600 text-white" : "text-muted-foreground hover:text-foreground"}`}
        >
          IT
        </button>
        <button
          data-testid="lang-en"
          onClick={() => setLang("English")}
          className={`px-2.5 py-1 text-xs font-semibold rounded-full transition-colors ${!isIT && isEN ? "bg-blue-600 text-white" : "text-muted-foreground hover:text-foreground"}`}
        >
          EN
        </button>
      </div>
      <a
        data-testid="whatsapp-top"
        href={CONTACTS.whatsappHref}
        target="_blank"
        rel="noopener noreferrer"
        title="Chat su WhatsApp"
        aria-label="Chat su WhatsApp"
        className="flex items-center justify-center w-9 h-9 rounded-full border border-border text-emerald-400 hover:border-emerald-500/60 hover:bg-emerald-500/10 transition-colors shrink-0"
      >
        <WhatsAppIcon className="w-4 h-4" />
      </a>
    </div>
  );
};

const Avatar = ({ user }) => {
  const initial = (user.name || user.email || "?").trim().charAt(0).toUpperCase();
  if (user.picture) {
    return <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" referrerPolicy="no-referrer" onError={(e) => { e.currentTarget.style.display = "none"; }} />;
  }
  return (
    <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/15 border border-primary/40 text-primary text-xs font-semibold">
      {initial}
    </span>
  );
};

const ProfileMenu = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (!user) {
    return (
      <>
        <button
          data-testid="signin-btn"
          onClick={() => setAuthOpen(true)}
          className="flex items-center gap-2 text-xs tracking-[0.12em] uppercase text-foreground border border-border hover:border-primary/40 hover:bg-secondary transition-colors px-3 py-2 rounded-full"
        >
          <LogIn strokeWidth={1.6} className="w-4 h-4" />
          <span className="hidden sm:inline">Sign in</span>
        </button>
        <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
      </>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="profile-menu-btn"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-full pl-1 pr-2 py-0.5 hover:bg-secondary transition-colors"
      >
        <Avatar user={user} />
        <ChevronDown strokeWidth={1.8} className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div data-testid="profile-menu" className="absolute right-0 mt-2 w-56 bg-card border border-border rounded-xl shadow-2xl p-2 z-50">
          <div className="px-3 py-2 border-b border-border mb-1">
            <p className="text-sm font-medium text-foreground truncate">{user.name || "Signed in"}</p>
            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
          </div>
          <div className="px-3 py-1.5 flex items-center justify-between text-xs mb-1">
            <span className="text-muted-foreground tracking-[0.12em] uppercase">Plan</span>
            <span data-testid="profile-plan" className={`tracking-[0.12em] uppercase ${user.plan && user.plan !== "free" ? "text-primary" : "text-foreground"}`}>{user.plan || "free"}</span>
          </div>
          {(!user.plan || user.plan === "free") && (
            <Link
              data-testid="upgrade-link"
              to="/pricing"
              onClick={() => setOpen(false)}
              className="w-full flex items-center gap-2 text-left text-sm px-3 py-2 rounded-lg text-primary hover:bg-secondary transition-colors mb-1"
            >
              <Sparkles strokeWidth={1.7} className="w-4 h-4" /> Upgrade to Pro
            </Link>
          )}
          <button
            data-testid="signout-btn"
            onClick={() => { setOpen(false); logout(); }}
            className="w-full flex items-center gap-2 text-left text-sm px-3 py-2 rounded-lg text-foreground hover:bg-secondary transition-colors"
          >
            <LogOut strokeWidth={1.6} className="w-4 h-4" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
};

const MobileMenu = ({ onLogoClick, onOpenMemory, onContact, memoryCount }) => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const close = () => setOpen(false);
  const itemCls = "w-full flex items-center gap-3 text-left text-sm px-3 py-3 rounded-lg hover:bg-secondary transition-colors";

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="mobile-menu-btn"
        onClick={() => setOpen((o) => !o)}
        aria-label="Menu"
        className="flex items-center justify-center w-10 h-10 rounded-full border border-border text-foreground hover:border-primary/40 hover:bg-secondary transition-colors"
      >
        {open ? <X strokeWidth={1.8} className="w-5 h-5" /> : <Menu strokeWidth={1.8} className="w-5 h-5" />}
      </button>
      {open && (
        <div data-testid="mobile-menu" className="absolute right-0 mt-2 w-64 bg-card border border-border rounded-xl shadow-2xl p-2 z-50">
          {user && (
            <div className="px-3 py-2 border-b border-border mb-1">
              <p className="text-sm font-medium text-foreground truncate">{user.name || "Signed in"}</p>
              <p className="text-xs text-muted-foreground truncate">{user.email}</p>
              <p className="text-[11px] tracking-[0.12em] uppercase mt-1 text-primary">{user.plan || "free"} plan</p>
            </div>
          )}
          <button data-testid="mobile-new-btn" onClick={() => { close(); onLogoClick?.(); }} className={`${itemCls} text-primary`}>
            <Plus strokeWidth={1.8} className="w-4 h-4" /> New session
          </button>
          <Link data-testid="mobile-pricing-link" to="/pricing" onClick={close} className={`${itemCls} text-primary`}>
            <Sparkles strokeWidth={1.7} className="w-4 h-4" /> Pricing
          </Link>
          <button data-testid="mobile-memory-btn" onClick={() => { close(); onOpenMemory?.(); }} className={`${itemCls} text-foreground`}>
            <Brain strokeWidth={1.6} className="w-4 h-4" /> Memory{memoryCount ? ` · ${memoryCount}` : ""}
          </button>
          {onContact && (
            <button data-testid="mobile-contact-btn" onClick={() => { close(); onContact?.(); }} className={`${itemCls} text-foreground`}>
              <Mail strokeWidth={1.6} className="w-4 h-4" /> Contact
            </button>
          )}
          <Link data-testid="mobile-control-link" to="/control" onClick={close} className={`${itemCls} text-foreground`}>
            <ShieldCheck strokeWidth={1.6} className="w-4 h-4" /> Control
          </Link>
          <div className="border-t border-border my-1" />
          {user ? (
            <>
              {(!user.plan || user.plan === "free") && (
                <Link data-testid="mobile-upgrade-link" to="/pricing" onClick={close} className={`${itemCls} text-primary`}>
                  <Sparkles strokeWidth={1.7} className="w-4 h-4" /> Upgrade to Pro
                </Link>
              )}
              <button data-testid="mobile-signout-btn" onClick={() => { close(); logout(); }} className={`${itemCls} text-foreground`}>
                <LogOut strokeWidth={1.6} className="w-4 h-4" /> Sign out
              </button>
            </>
          ) : (
            <button data-testid="mobile-signin-btn" onClick={() => { setOpen(false); setAuthOpen(true); }} className={`${itemCls} text-foreground`}>
              <LogIn strokeWidth={1.6} className="w-4 h-4" /> Sign in
            </button>
          )}
        </div>
      )}
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
    </div>
  );
};

export const TopBar = ({ onLogoClick, onOpenMemory, onContact, memoryCount, center, settings, onSettingsChange }) => (
  <header className="fixed top-0 inset-x-0 z-40 h-16 nexx-glass border-b border-border flex items-center justify-between gap-3 px-4 sm:px-8">
    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
      <button data-testid="rexxpons-logo" onClick={onLogoClick} className="flex items-center gap-2.5 group shrink-0 min-w-0">
        <span className="relative flex items-center justify-center w-8 h-8 rounded-md border border-primary/40 bg-primary/10 group-hover:bg-primary/20 transition-colors shrink-0">
          <span className="text-primary font-heading text-sm font-semibold">R</span>
        </span>
        <span className="flex flex-col leading-none text-left min-w-0">
          <Wordmark className="text-base sm:text-lg text-foreground" />
          <span className="hidden sm:block text-[9px] tracking-[0.22em] uppercase text-muted-foreground mt-1">Beyond Search, Into Understanding</span>
        </span>
      </button>
      <HeaderLangWhatsApp settings={settings} onSettingsChange={onSettingsChange} />
    </div>

    <div className="hidden lg:flex flex-1 items-center justify-center px-6 min-w-0">{center}</div>

    {/* Desktop / tablet actions */}
    <div className="hidden md:flex items-center gap-1.5 sm:gap-2">
      <Link
        data-testid="pricing-link"
        to="/pricing"
        className="flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-primary border border-primary/40 bg-primary/10 hover:bg-primary/20 transition-colors px-3 py-2 rounded-full"
      >
        <Sparkles strokeWidth={1.6} className="w-4 h-4" />
        <span className="hidden lg:inline">Pricing</span>
      </Link>
      {onContact && (
        <button
          data-testid="contact-btn"
          onClick={onContact}
          className="flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-muted-foreground hover:text-foreground transition-colors px-3 py-2 rounded-full hover:bg-secondary"
        >
          <Mail strokeWidth={1.5} className="w-4 h-4" />
          <span className="hidden lg:inline">Contact</span>
        </button>
      )}
      <Link
        data-testid="control-panel-link"
        to="/control"
        className="flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-muted-foreground hover:text-foreground transition-colors px-3 py-2 rounded-full hover:bg-secondary"
      >
        <ShieldCheck strokeWidth={1.5} className="w-4 h-4" />
        <span className="hidden lg:inline">Control</span>
      </Link>
      <button
        data-testid="open-memory-btn"
        onClick={onOpenMemory}
        className="flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-muted-foreground hover:text-foreground transition-colors px-3 py-2 rounded-full hover:bg-secondary"
      >
        <Brain strokeWidth={1.5} className="w-4 h-4" />
        <span className="hidden lg:inline">Memory{memoryCount ? ` · ${memoryCount}` : ""}</span>
      </button>
      <button
        data-testid="new-session-btn"
        onClick={onLogoClick}
        className="flex items-center gap-1.5 text-xs tracking-[0.12em] uppercase text-primary border border-primary/40 hover:bg-primary/10 transition-colors px-3 py-2 rounded-full"
      >
        <Plus strokeWidth={2} className="w-3.5 h-3.5" /> New
      </button>
      <ProfileMenu />
    </div>

    {/* Mobile actions */}
    <div className="flex md:hidden items-center gap-2 shrink-0">
      <MobileMenu onLogoClick={onLogoClick} onOpenMemory={onOpenMemory} onContact={onContact} memoryCount={memoryCount} />
    </div>
  </header>
);
